import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  Paper, 
  Container,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';

export const ChangePassword: React.FC = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { changePassword } = useAuth();
  const { t } = useLocalization();
  const navigate = useNavigate();

  const handleTogglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t.auth.changePassword.passwordsNotMatch);
      setIsLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      setIsLoading(false);
      return;
    }

    try {
      await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err) {
      // Show the actual error message from the API if available, otherwise use localized error
      const errorMessage = err instanceof Error && err.message !== 'Password change failed' 
        ? err.message 
        : t.auth.changePassword.error;
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={ {
       marginTop:"3rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        } }
      >
        <Paper
          elevation={ 4 }
          sx={ {
            p: 4,
            width: "100%",
            borderRadius: 3,
          } }
        >
          {/* Header */ }
          <Box sx={ { display: "flex", alignItems: "center", mb: 3 } }>
            <IconButton onClick={ () => navigate( "/profile" ) } sx={ { mr: 1 } }>
              <ArrowBackIcon />
            </IconButton>

            <Box>
              <Typography variant="h5" sx={ { fontWeight: 700, color: "primary.main" } }>
                { t.auth.changePassword.title }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Update your account password to keep it secure
              </Typography>
            </Box>
          </Box>

          {/* Alerts */ }
          { error && (
            <Alert severity="error" sx={ { mb: 2 } }>
              { error }
            </Alert>
          ) }

          { success && (
            <Alert severity="success" sx={ { mb: 2 } }>
              { t.auth.changePassword.success }
            </Alert>
          ) }

          {/* Section Title */ }
          <Box sx={ { display: "flex", alignItems: "center", mb: 2 } }>
            <LockIcon sx={ { mr: 1.5, color: "primary.main" } } />
            <Typography variant="subtitle1" fontWeight={ 600 }>
              Password Information
            </Typography>
          </Box>

          <Divider sx={ { mb: 3 } } />

          {/* Form */ }
          <Box component="form" onSubmit={ handleSubmit }>
            <TextField
              fullWidth
              label={ t.auth.changePassword.currentPassword }
              type={ showPasswords.current ? "text" : "password" }
              placeholder="Enter your current password"
              value={ formData.currentPassword }
              onChange={ ( e ) =>
                setFormData( { ...formData, currentPassword: e.target.value } )
              }
              margin="normal"
              required
              InputProps={ {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={ () => handleTogglePasswordVisibility( "current" ) }>
                      { showPasswords.current ? <VisibilityOff /> : <Visibility /> }
                    </IconButton>
                  </InputAdornment>
                ),
              } }
            />

            <TextField
              fullWidth
              label={ t.auth.changePassword.newPassword }
              type={ showPasswords.new ? "text" : "password" }
              placeholder="Enter your new password"
              value={ formData.newPassword }
              onChange={ ( e ) =>
                setFormData( { ...formData, newPassword: e.target.value } )
              }
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
              InputProps={ {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={ () => handleTogglePasswordVisibility( "new" ) }>
                      { showPasswords.new ? <VisibilityOff /> : <Visibility /> }
                    </IconButton>
                  </InputAdornment>
                ),
              } }
            />

            <TextField
              fullWidth
              label={ t.auth.changePassword.confirmPassword }
              type={ showPasswords.confirm ? "text" : "password" }
              placeholder="Confirm your new password"
              value={ formData.confirmPassword }
              onChange={ ( e ) =>
                setFormData( { ...formData, confirmPassword: e.target.value } )
              }
              margin="normal"
              required
              InputProps={ {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={ () => handleTogglePasswordVisibility( "confirm" ) }>
                      { showPasswords.confirm ? <VisibilityOff /> : <Visibility /> }
                    </IconButton>
                  </InputAdornment>
                ),
              } }
            />

            {/* Buttons */ }
            <Box sx={ { mt: 4, display: "flex", gap: 2 } }>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={ isLoading }
                sx={ {
                  px: 4,
                  py: 1.4,
                  fontWeight: 600,
                  flex: 1,
                  textTransform: 'none',
                } }
              >
                { isLoading ? "Updating..." : t.auth.changePassword.submit }
              </Button>

              <Button
                variant="outlined"
                onClick={ () => navigate( "/" ) }
                sx={ {
                  px: 4,
                  py: 1.4,
                  flex: 1,
                  textTransform: 'none',
                } }
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>

  );
}; 